import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { IPicking } from '@app/models/picking.interface';
import { expand, first, map, reduce, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectCurrentItemState } from '@app/modules/organization/store/selectors/organization.selectors';
import { selectPickingsState } from '@app/modules/pickings/store/selectors/pickings.selectors';
import { SUPPRESS_TOASTR } from '@app/interceptors/error/http-error.interceptor';

@Injectable({
  providedIn: 'root'
})

export class PickingsService {
  public endpoint: string = `${environment.apiUrl}/pickings`;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;

  constructor(
    private _http: HttpClient,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
  }

  getPickings(sortType, direction, query?, currentPage?,pageSize?) {
    this.store$.select(selectPickingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}?page=${currentPage}`, {
      params: {
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }

  getPickingProductsWithPage(sortType, direction, query?, currentPage?,pageSize?) {
    return this._http.get<any>(`${this.endpoint}/search-products?page=${currentPage}`, {
      params: {
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query
      }
    });
  }

  getPickingProductsMovementHistoryWithPage(sortType, direction, query?, currentPage?,pageSize?) {
    return this._http.get<any>(`${this.endpoint}/search-products-history?page=${currentPage}`, {
      params: {
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query
      }
    });
  }

  getPickingProductOnHand(id:number): Observable<any>  {
    return this._http.get<any>(`${this.endpoint}/product-onhand/${id}`);
  }

  getProductActivity(prdID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/product-activity/${prdID}`);
  }

  getPicking(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  getPickingsPath(id: number, locationId:number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/pickings-path/${id}?locationId=${locationId}`)
  }

  onSave(picking: IPicking, quiet: boolean = false): Observable<any> {
    return this._http.post<any>(this.endpoint, picking, {
      context: new HttpContext().set(SUPPRESS_TOASTR, quiet)
    });
  }

  onOverride(picking: IPicking): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/override`, picking);
  }

  // One page of the in-stock serial numbers of a product in one storage (page is 0-based, pageSize max 200).
  getSerialsInStockPage(prdID: number, storageStructureID: number, page: number, pageSize: number = 200): Observable<any> {
    return this._http.get<any>(`${environment.apiUrl}/serial-numbers/in-stock`, {
      params: { prdID: String(prdID), storageStructureID: String(storageStructureID), page: String(page), pageSize: String(pageSize) }
    });
  }

  // Every in-stock serial number of a product in one storage, all pages joined.
  getAllSerialsInStock(prdID: number, storageStructureID: number): Observable<any[]> {
    const pageSize = 200;
    let page = 0;
    return this.getSerialsInStockPage(prdID, storageStructureID, page, pageSize).pipe(
      expand(res => {
        page++;
        return (res?.serialNumbers?.length || 0) >= pageSize && page * pageSize < (res.total || 0)
          ? this.getSerialsInStockPage(prdID, storageStructureID, page, pageSize)
          : EMPTY;
      }),
      reduce((all: any[], res: any) => all.concat(res?.serialNumbers || []), [])
    );
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  updatePicking(picking) {
    return this._http.put<any>(`${this.endpoint}/${picking.productMovementHistoryID}`, picking)
  }

  onDelete(id: number, reason: string) {
    const body = { reason };
    return this._http.delete(`${this.endpoint}/${id}`, { body });
  }

  searchPickings(sort, direction, query: string): Observable<any> {
    this.store$.select(selectPickingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  importPickings(pickings: {}[]) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import`, {
      pickings
    }, { headers });
  }

  confirmImport(pickings: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirm`, {
      pickings
    });
  }

  confirmImportSkusBarcodes(skusBarcodes: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirmSkusBarcodes`, {
      skusBarcodes
    });
  }


  onSortPickings(type: string, category?) {
    return this._http.get<any>(`${this.endpoint}/sort/${type}`, {
      params: {
        category: category
      }
    }).pipe(first()).subscribe((data) => {
      this.totalData$.next(data.total);
    });
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  batchDelete(selected, reason): Observable<any> {
    return this._http.post(`${this.endpoint}/batchDelete`, { ids: selected, reason:reason });
  }

  onSetIsAllowedToLeave(res) {
    return this.isAllowedToLeaveData$.next(res);
  }

  onGenerateExcel(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/pickings/export`);
  }

  onSort(arr, direction) {
    let type = 'name';

    if (arr.length <= 1) {
      return arr;
    }

    var pivot = arr[0];

    var left = [];
    var right = [];

    for (var i = 1; i < arr.length; i++) {
      if (direction == 'desc') {
        arr[i][type] < pivot[type] ? right.push(arr[i]) : left.push(arr[i]);
      } else {
        arr[i][type] < pivot[type] ? left.push(arr[i]) : right.push(arr[i]);
      }

    }

    return this.onSort(left, direction).concat(pivot, this.onSort(right, direction));
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

  // Asset Borrow/Return Methods
  getAssetBorrows(prdID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/asset-borrows/${prdID}`);
  }

  getAssetBorrowHistory(prdID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/asset-borrow-history/${prdID}`);
  }

  createAssetBorrow(data: any): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/asset-borrow`, data);
  }

  returnAsset(productMovementHistoryID: number, quantity: number): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/asset-return`, {
      productMovementHistoryID,
      quantity
    });
  }

  getOverdueBorrows(): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/overdue-borrows`);
  }

  getUserActiveBorrows(usrID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/user-borrows/${usrID}`);
  }

  assetStockAdjustment(data: any): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/asset-stock-adjustment`, data);
  }

  uploadAssetReturnPhoto(productMovementHistoryID: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post<any>(`${this.endpoint}/asset-return-photo/${productMovementHistoryID}`, formData);
  }

  getAssetReturnPhoto(productMovementHistoryID: number, index: number): Observable<Blob> {
    return this._http.get(`${this.endpoint}/asset-return-photo/${productMovementHistoryID}/${index}`, { responseType: 'blob' });
  }
}

